import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { ProductLine } from '../models';
import { SetProductLines, SetProductLineLoader, SetActiveProductLines } from '../store/actions';
import { selectFilterState } from '../store/selectors';
import { ProductLineService, ProductLineRequest } from '@cemex-core/cmx-api';
import { GlobalFiltersPersistenceService } from './global-filters-persistence.service';
import { GlobalFiltersState } from '../models';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { PRODUCT_LINE_ALPHA_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';

@Injectable({
  providedIn: 'root',
})
export class GlobalFiltersProductLinesService {
  constructor(
    private productLineService: ProductLineService,
    private store: Store<GlobalFiltersState>,
    private persistence: GlobalFiltersPersistenceService,
    private featureToggle: FeatureToggleService,
  ) {}

  async getProductLines(legalEntityId: number, country: string) {
    const shouldFetch = await this.shouldFetchProductLines(legalEntityId);
    if (!shouldFetch) {
      return;
    }
    try {
      this.store.dispatch(new SetProductLineLoader(true));
      const productLines = await this.fetchAndMapProductLines(country);
      this.store.dispatch(new SetProductLines(legalEntityId, productLines));
      this.store.dispatch(new SetProductLineLoader(false));
    } catch (error) {
      this.store.dispatch(new SetProductLineLoader(false));
    }
  }

  async getActiveProductLines(legalEntityId: number, country: string, jobsiteId: number): Promise<number[]> {
    const { shouldFetch, productLinesIds } = await this.shouldFetchActiveProductLines(jobsiteId);
    if (!shouldFetch) {
      if (productLinesIds) {
        return productLinesIds;
      }
      return [];
    }
    try {
      this.store.dispatch(new SetProductLineLoader(true));
      const productLines = await this.fetchAndMapProductLines(country, jobsiteId, legalEntityId);
      const newProductLinesIds = productLines.map((item) => item.id);
      this.persistence.saveActiveProductLines(jobsiteId, newProductLinesIds);
      this.store.dispatch(new SetActiveProductLines(jobsiteId, newProductLinesIds));
      this.store.dispatch(new SetProductLineLoader(false));
      return newProductLinesIds;
    } catch (error) {
      this.store.dispatch(new SetProductLineLoader(false));
      return [];
    }
  }
  private async shouldFetchProductLines(legalEntityId: number): Promise<boolean> {
    try {
      const filterState = await this.store.pipe(select(selectFilterState), take(1)).toPromise();
      const productLines = filterState.filterPerCustomer[legalEntityId].productLines;
      if (productLines) {
        return false;
      }
      return true;
    } catch (error) {
      return true;
    }
  }

  private async shouldFetchActiveProductLines(
    jobsiteId: number,
  ): Promise<{ shouldFetch: boolean; productLinesIds?: number[] }> {
    try {
      if (!jobsiteId) {
        return { shouldFetch: false };
      }
      const filterState = await this.store.pipe(select(selectFilterState), take(1)).toPromise();
      const activePorductLinesIds = filterState.activeProductLinesIdsPerJobsite[jobsiteId];
      if (activePorductLinesIds) {
        return { shouldFetch: false, productLinesIds: activePorductLinesIds };
      }
      return { shouldFetch: true };
    } catch (error) {
      return { shouldFetch: true };
    }
  }

  /**
   * Makes request to get product lines for given jobsiteId
   * This response also defines enabled/disabled options in the product lines dropdown
   * @param countryCode
   * @param jobsiteId
   */
  private async fetchAndMapProductLines(
    countryCode: string,
    jobsiteId?: number,
    legalEntityId?: number,
  ): Promise<ProductLine[]> {
    const body: ProductLineRequest = jobsiteId
      ? jobsiteId === 1
        ? { customerId: legalEntityId }
        : { jobsiteId }
      : { countryCode };
    const response = await this.productLineService.getProductLines(body).toPromise();
    const productLinesApi = response.productLines;
    const admixturesEnabled = this.featureToggle.isFeatureEnabled(Features.ENABLE_ADMIXTURES_PRODUCT_TYPE);
    const productLines: ProductLine[] = productLinesApi
      .filter((productLine) =>
        !admixturesEnabled ? productLine.productLineCode !== PRODUCT_LINE_ALPHA_CODE.ADMIXTURES : true,
      )
      .map((productLineApi): ProductLine => {
        return {
          code: productLineApi.productLineCode,
          id: productLineApi.productLineId,
          name: productLineApi.productLineDesc,
        };
      });
    return productLines;
  }
}
